import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter, Routes, Route, Navigate, useSearchParams, useParams } from 'react-router-dom';
import './index.css';
import App from './App';
import OAuthCallback from './components/OAuthCallback';
import PresenterView from './components/PresenterView';
import PresentationView from './components/PresentationView';
import RemotePresenterClient from './components/RemotePresenterClient';
import { isValidRemoteRoomCode, normalizeRemoteRoomCode } from './utils/remoteJoinPath';

// Suppress harmless errors from Google Slides iframes
const originalError = console.error;
const originalWarn = console.warn;

console.error = (...args) => {
  const message = args.join(' ');
  // Suppress chrome-extension://invalid errors from Google Slides iframes
  if (message.includes('chrome-extension://invalid') || 
      (message.includes('net::ERR_FAILED') && message.includes('chrome-extension'))) {
    return; // Suppress this error
  }
  originalError.apply(console, args);
};

console.warn = (...args) => {
  const message = args.join(' ');
  // Suppress target-densitydpi warnings from Google Slides iframes
  if (message.includes('target-densitydpi')) {
    return; // Suppress this warning
  }
  originalWarn.apply(console, args);
};

// Suppress unhandled errors from iframes
window.addEventListener('error', (event) => {
  const message = event.message || '';
  const source = event.filename || '';
  // Suppress errors from Google Slides iframes
  if (message.includes('chrome-extension://invalid') ||
      (message.includes('net::ERR_FAILED') && source.includes('docs.google.com'))) {
    event.preventDefault();
    return false;
  }
}, true);

// Suppress unhandled promise rejections from iframes
window.addEventListener('unhandledrejection', (event) => {
  const message = event.reason?.message || String(event.reason || '');
  // Suppress promise rejections related to chrome-extension errors
  if (message.includes('chrome-extension://invalid') ||
      message.includes('net::ERR_FAILED')) {
    event.preventDefault();
    return false;
  }
});

/** `/?room=CODE` → short join URL `/{CODE}` (legacy query still supported on `/remote`). */
function RootEntry() {
  const [searchParams] = useSearchParams();
  const room = normalizeRemoteRoomCode(searchParams.get('room')?.trim() ?? '');
  if (isValidRemoteRoomCode(room)) {
    return <Navigate to={`/${room}`} replace />;
  }
  return <App />;
}

function RemoteJoinAtPath() {
  const { joinCode } = useParams();
  const normalized = normalizeRemoteRoomCode(joinCode || '');
  if (!isValidRemoteRoomCode(normalized)) {
    return <Navigate to="/" replace />;
  }
  /* Remount when the path code changes so bootstrap + session match the URL. */
  return <RemotePresenterClient key={normalized} pathRoomId={normalized} />;
}

/** `/remote?room=CODE` — same remount rule when the query changes. */
function RemotePresenterAtQuery() {
  const [searchParams] = useSearchParams();
  const room = normalizeRemoteRoomCode(searchParams.get('room')?.trim() ?? '');
  return <RemotePresenterClient key={room || '__remote__'} pathRoomId={room || undefined} />;
}

if (typeof window !== 'undefined' && 'serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    const base = (process.env.PUBLIC_URL || '').replace(/\/$/, '');
    navigator.serviceWorker.register(`${base}/deckpack-notify-sw.js`).catch(() => {
      /* join alerts still use page Notification when SW is unavailable */
    });
  });
}

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    <BrowserRouter>
    <Routes>
      <Route path="/" element={<RootEntry />} />
      <Route path="/oauth-callback" element={<OAuthCallback />} />
      <Route path="/presenter-view" element={<PresenterView />} />
      <Route path="/presentation-view" element={<PresentationView />} />
      <Route path="/remote" element={<RemotePresenterAtQuery />} />
      <Route path="/:joinCode" element={<RemoteJoinAtPath />} />
    </Routes>

    </BrowserRouter>
  </React.StrictMode>
);